import React, { useState, useEffect, useContext } from "react";
import { FaTimes } from "react-icons/fa"; // Close icon
import { UserContext } from "../context/UserContext"; // Access user details for emailId
import AddProject from "../sections/AddProject";

const ProjectAdmin = ({ onClose }) => {
  const [projects, setProjects] = useState([]); // Projects state
  const { emailId, isLoggedIn } = useContext(UserContext); // Get user details
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [showDeletePopup, setShowDeletePopup] = useState(null); // Tracks the project ID for delete popup
  const [showEditPopup, setShowEditPopup] = useState(null); // Tracks the project ID for edit popup
  const [showAdditionalMessagePopup, setShowAdditionalMessagePopup] =
    useState(null); // Tracks additional message popup
  const [additionalMessage, setAdditionalMessage] = useState(""); // Tracks message entered in the popup
  const [successMessage, setSuccessMessage] = useState(""); // Tracks success message for delete action
  const [selectedProjectUserEmail, setSelectedProjectUserEmail] = useState(""); // Tracks the user's email for a selected project
  const [editFormData, setEditFormData] = useState({
    target_amount: "",
    end_date: "",
    description: "",
    project_image: null,
  });
  const [searchTerm, setSearchTerm] = useState(""); // For dynamic project filtering

  // Fetch project list from API
  useEffect(() => {
    const fetchProjects = async () => {
      if (!isLoggedIn) return;
      setIsLoading(true);

      try {
        const apiEndpoint = emailId === "info@urcommunity.com.au"
          ? `/api/projectlist?type=project`  // Changed endpoint
          : `/api/projectlist?type=project&email=${emailId}`; // Changed endpoint and added type
        const response = await fetch(apiEndpoint);
        if (!response.ok) return;

        const data = await response.json();
        setProjects(data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [emailId, isLoggedIn]);

  // Fetch project details for editing
  const fetchProjectDetails = async (projectId) => {
    try {
      const response = await fetch(
        `/api/projectlist/${projectId}`
      );
      if (!response.ok) throw new Error("Failed to fetch project details");

      const data = await response.json();
      setEditFormData({
        target_amount: data.target_amount,
        end_date: data.end_date,
        description: data.description,
        project_image: null, // File input can't be pre-filled
      });
    } catch (error) {
      console.error("Error fetching project details:", error);
      alert("Failed to fetch project details. Please try again.");
    }
  };

  // Function to handle edit action
  const handleEdit = (projectId) => {
    setShowEditPopup(projectId); // Open the edit popup for the specific project
    fetchProjectDetails(projectId); // Fetch project details for editing
  };

  // Handle Save Changes in the edit form
  const handleSaveChanges = async (updatedProject) => {
    try {
      const response = await fetch(
        `/api/projectlist/${showEditPopup}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedProject),
        }
      );
      if (!response.ok) throw new Error("Failed to update project");

      setShowEditPopup(null); // Close the popup
      alert("Project updated successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes. Please try again.");
    }
  };

  // Handle Delete action
  const handleDelete = async (projectId) => {
    try {
      // Step 1: Fetch project details to get the owner's email
      const projectResponse = await fetch(`/api/projectlist/${projectId}`);
  
      if (!projectResponse.ok) {
        throw new Error("Failed to fetch project details");
      }
  
      const projectData = await projectResponse.json(); // Get project details
      console.log("Project Data Fetched:", projectData); // Debugging
  
      // Step 2: Set the project owner's email
      setSelectedProjectUserEmail(projectData.email_id);
      console.log("Selected Project User Email:", projectData.email_id); // Debugging
  
      // Step 3: Delete the project
      const deleteResponse = await fetch(`/api/projectlist/${projectId}`, {
        method: "DELETE",
      });
      localStorage.setItem("selectedProjectId", "60");
      if (!deleteResponse.ok) {
        throw new Error("Failed to delete project");
      }
  
      // Step 4: Remove the project from the state
      setProjects((prevProjects) => prevProjects.filter((project) => project.project_id !== projectId));
  
      // Step 5: Show success message
      setSuccessMessage("Your project has been successfully deleted.");
      setTimeout(() => setSuccessMessage(""), 5000);
  
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Failed to delete project. Please try again.");
    } finally {
      setShowDeletePopup(null);
      setShowAdditionalMessagePopup(null);
    }
  };
  

  const notifyAdmin = async (projectId, userEmail,projectTitle) => {
    try {
      console.log("Sending email to:", userEmail); // Debugging
  
      const response = await fetch(
        `/api/admin-notification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            projectTitle,
            reason: additionalMessage,
            userEmail, // Pass the email directly
          }),
        }
      );
  
      if (!response.ok) throw new Error("Failed to send admin notification");
  
      alert("Admin notification sent successfully!");
    } catch (error) {
      console.error("Error notifying admin:", error);
      alert("Failed to send admin notification. Please try again.");
    }
  };
  

  // Handle Submit button (deletes project and sends admin notification)
  const handleAdminSubmit = async (projectId) => {
    try {
      // Step 1: Fetch project details to get owner's email before deletion
      const projectResponse = await fetch(`/api/projectlist/${projectId}`);
      if (!projectResponse.ok) {
        throw new Error("Failed to fetch project details");
      }
      const projectData = await projectResponse.json(); // Extract project details
      console.log("Project Data Fetched:", projectData); // Debugging
  
      // Step 2: Delete the project
      await handleDelete(projectId);
  
      // Step 3: Call notifyAdmin using the email directly
      console.log("Notifying Admin for Email:", projectData.email_id);
      await notifyAdmin(projectId, projectData.email_id, projectData.project_title);
  
    } catch (error) {
      console.error("Error handling admin submit:", error);
    }
  };
  

  // Function to filter projects dynamically as user types
  const getFilteredProjects = () => {
    const searchValue = searchTerm.toLowerCase().trim();

    if (!searchValue) return projects; // If no input, show all 

    return projects.filter((project) =>
      [project.project_title, project.category, project.end_date].some((field) =>
        field?.toString().toLowerCase().includes(searchValue)
      )
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)", // Overlay
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999,
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "10px",
          padding: "20px",
          width: "95%",
          maxWidth: "800px",
          maxHeight: "80vh", // Limit height for scrollable content
          overflowY: "auto",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ margin: 0 }}>Projects Board</h3>
          <FaTimes
            onClick={onClose}
            style={{
              cursor: "pointer",
              fontSize: "1.5rem",
              color: "#333",
            }}
          />
        </div>

        {/* Success Message */}
        {successMessage && (
          <div
            style={{
              textAlign: "center",
              marginBottom: "20px",
              padding: "10px",
              backgroundColor: "#d4edda",
              color: "#155724",
              borderRadius: "5px",
            }}
          >
            {successMessage}
          </div>
        )}

        {/* Search Bar */}
        <div
          className="search-bar"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <input
            type="text"
            placeholder="Search projects..."
            className="search-input"
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              outline: "none",
            }}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          />
        </div>

        {/* Project List */}
        {isLoading ? (
          <p style={{ textAlign: "center", color: "#555" }}>
            Loading projects...
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
              gap: "20px",
            }}
          >
            {getFilteredProjects().map((project) => (
              <div
                key={project.project_id}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                  padding: "10px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  position: "relative",
                }}
              >
                {/* Project Info */}
                <div>
                  <h5
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {project.project_title}
                  </h5>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#555",
                      marginBottom: "5px",
                    }}
                  >
                    Category: {project.category}
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#555",
                      marginBottom: "5px",
                    }}
                  >
                    End Date: {project.end_date}
                  </p>
                </div>

                {/* Progress Bar */}
                <div
                  style={{
                    height: "10px",
                    background: "#e0e0e0",
                    borderRadius: "5px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.min(
                        ((project.total_raised || 0) /
                          (project.target_amount || 1)) *
                          100,
                        100
                      )}%`,
                      background: "linear-gradient(to right, #007bff, #00d4ff)",
                      borderRadius: "5px",
                      transition: "width 0.3s ease-in-out",
                    }}
                  ></div>
                </div>
                <p
                  style={{
                    marginTop: "5px",
                    fontSize: "12px",
                    color: "#777",
                  }}
                >
                  ${project.total_raised || 0} raised of $
                  {project.target_amount || 0}
                </p>

                {/* Action Buttons */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  {emailId !== "info@urcommunity.com.au" && (
                    <button
                      onClick={() => handleEdit(project.project_id)}
                      style={{
                        backgroundColor: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        padding: "5px 10px",
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => setShowDeletePopup(project.project_id)}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      border: "none",
                      borderRadius: "5px",
                      padding: "5px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>

                {/* Delete Confirmation Popup */}
                {showDeletePopup === project.project_id && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                      padding: "20px",
                      textAlign: "center",
                      zIndex: 1000,
                      width: "100%",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        marginBottom: "20px",
                      }}
                    >
                      Are you sure you want to delete this{" "}
                      <strong>{project.project_title}</strong>?
                    </p>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      <button
                        onClick={() => setShowDeletePopup(null)}
                        style={{
                          backgroundColor: "#007bff",
                          color: "#fff",
                          border: "none",
                          borderRadius: "5px",
                          padding: "10px 20px",
                          cursor: "pointer",
                        }}
                      >
                        No
                      </button>
                      <button
                        onClick={() => {
                          if (emailId === "info@urcommunity.com.au") {
                            setShowAdditionalMessagePopup(project.project_id);
                          } else {
                            handleDelete(project.project_id);
                          }
                        }}
                        style={{
                          backgroundColor: "#dc3545",
                          color: "#fff",
                          border: "none",
                          borderRadius: "5px",
                          padding: "10px 20px",
                          cursor: "pointer",
                        }}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                )}

                {showEditPopup && (
                  <AddProject
                    onClose={() => setShowEditPopup()}
                    project={projects.find(
                      (p) => p.project_id === showEditPopup
                    )}
                    handleSubmit={handleSaveChanges}
                  />
                )}
              
                {/* Additional Message Popup */}
                {showAdditionalMessagePopup === project.project_id && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                      padding: "20px",
                      zIndex: 1001,
                      width: "300px",
                      textAlign: "center",
                    }}
                  >
                    <p style={{ marginBottom: "20px", fontWeight: "bold" }}>
                      Do you want to add any additional message to the user?
                    </p>
                    <textarea
                      placeholder="Message box"
                      value={additionalMessage}
                      onChange={(e) => setAdditionalMessage(e.target.value)}
                      style={{
                        width: "100%",
                        height: "80px",
                        padding: "10px",
                        fontSize: "14px",
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                        marginBottom: "10px",
                      }}
                    />
                    <button
                      onClick={() => {
                        handleAdminSubmit(showAdditionalMessagePopup); // Proceed with deletion
                        console.log("Additional Message:", additionalMessage); // Log the message
                      }}
                      style={{
                        backgroundColor: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        padding: "10px 20px",
                        cursor: "pointer",
                      }}
                    >
                      Submit
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* No Projects Available */}
        {!isLoading && projects.length === 0 && (
          <p style={{ textAlign: "center", color: "#555" }}>
            You don't have any projects yet. Ready to get started? Click "Add
            button" in Campaign to begin your journey!
          </p>
        )}
      </div>
    </div>
  );
};

export default ProjectAdmin;
